import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { App } from '@capacitor/app';

const WORKOUT_NOTIFICATION_ID = 9001;
const REST_COMPLETE_NOTIFICATION_ID = 9002;

// Internal state — shared by both paths
let _disabled = false;              // true if permission denied
let _isBackground = false;          // true when app is not in foreground
let _lastInfo = null;               // last workout snapshot

// Native-only state
let _listenerRegistered = false;
let _appStateListenerRegistered = false;

// Web/PWA-only state
let _webWorkoutNotification = null; // fallback Notification object (desktop)
let _visibilityListenerRegistered = false;

// Get the active service worker registration (required for Chrome Android)
async function getSwRegistration() {
  if (!('serviceWorker' in navigator)) return null;
  try { return await navigator.serviceWorker.ready; } catch { return null; }
}

const isNative = () => Capacitor.isNativePlatform();

// Format seconds as M:SS
function formatSeconds(s) {
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// Build notification body from info snapshot
function buildBody(info) {
  if (info.isResting) {
    // Static snapshot — don't show "remaining" since it won't count down
    return `Resting ${formatSeconds(info.restSecondsLeft)} · ${info.exerciseName}`;
  }
  return `${info.exerciseName} · Set ${info.setNumber} of ${info.totalSets}`;
}

// ─── Native (Capacitor) path ────────────────────────────────

async function postWorkoutNotification(info) {
  if (!isNative() || _disabled) return;
  try {
    // Cancel existing first so Android replaces it cleanly
    await LocalNotifications.cancel({ notifications: [{ id: WORKOUT_NOTIFICATION_ID }] });
    await LocalNotifications.schedule({
      notifications: [
        {
          id: WORKOUT_NOTIFICATION_ID,
          title: '💪 Workout in Progress',
          body: buildBody(info),
          schedule: { at: new Date(Date.now() + 50) }, // +50ms: LocalNotifications requires a future timestamp
          ongoing: true,       // Android: cannot swipe away (like Spotify)
          autoCancel: false,
          smallIcon: 'ic_stat_icon',
          iconColor: '#FF5C00',
          extra: { type: 'workout_in_progress' },
        },
      ],
    });
  } catch (e) {
    console.warn('[workoutNotification] postWorkoutNotification error', e);
  }
}

function registerTapListener() {
  if (_listenerRegistered) return;
  _listenerRegistered = true;
  if (!isNative()) return;

  LocalNotifications.addListener('localNotificationActionPerformed', (action) => {
    const id = action.notification?.id;
    if (id === WORKOUT_NOTIFICATION_ID || id === REST_COMPLETE_NOTIFICATION_ID) {
      window.location.href = '/workout';
    }
  });
}

function registerAppStateListener() {
  if (!isNative() || _appStateListenerRegistered) return;
  _appStateListenerRegistered = true;

  App.addListener('appStateChange', async ({ isActive }) => {
    _isBackground = !isActive;
    if (isActive) {
      try {
        await LocalNotifications.cancel({ notifications: [{ id: WORKOUT_NOTIFICATION_ID }] });
      } catch {}
    } else {
      if (_lastInfo) {
        await postWorkoutNotification(_lastInfo);
      }
    }
  });
}

// ─── Web / PWA path ─────────────────────────────────────────

async function requestWebPermission() {
  if (typeof Notification === 'undefined') {
    _disabled = true;
    return false;
  }
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') {
    _disabled = true;
    return false;
  }
  try {
    const result = await Notification.requestPermission();
    if (result !== 'granted') {
      _disabled = true;
      return false;
    }
    return true;
  } catch {
    _disabled = true;
    return false;
  }
}

async function postWebWorkoutNotification(info) {
  if (_disabled || typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  try {
    const reg = await getSwRegistration();
    if (reg) {
      // Service Worker path — required for Chrome Android PWA
      await reg.showNotification('💪 Workout in Progress', {
        body: buildBody(info),
        tag: 'workout-in-progress', // same tag = replaces previous, no spam
        renotify: false,
        silent: true,
        icon: '/icon-192.png',
        badge: '/icon-192.png',
        data: { url: '/workout' },
        actions: [{ action: 'open', title: 'Return to Workout' }],
      });
    } else {
      // Fallback: Notification constructor (desktop browsers)
      if (_webWorkoutNotification) { _webWorkoutNotification.close(); }
      const n = new Notification('💪 Workout in Progress', {
        body: buildBody(info),
        tag: 'workout-in-progress',
        silent: true,
        icon: '/icon-192.png',
      });
      n.onclick = () => { window.focus(); n.close(); };
      _webWorkoutNotification = n;
    }
  } catch (e) {
    console.warn('[workoutNotification] postWebWorkoutNotification error', e);
  }
}

async function closeWebWorkoutNotification() {
  if (_webWorkoutNotification) {
    _webWorkoutNotification.close();
    _webWorkoutNotification = null;
  }
  try {
    const reg = await getSwRegistration();
    if (reg) {
      const notifications = await reg.getNotifications({ tag: 'workout-in-progress' });
      notifications.forEach(n => n.close());
    }
  } catch {}
}

function registerVisibilityListener() {
  if (_visibilityListenerRegistered) return;
  _visibilityListenerRegistered = true;

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      _isBackground = true;
      if (_lastInfo) postWebWorkoutNotification(_lastInfo);
    } else {
      _isBackground = false;
      closeWebWorkoutNotification();
    }
  });
}

// ─── Public API ─────────────────────────────────────────────

/**
 * Call this directly from the "Start Workout" button handler (user gesture context).
 * Chrome/Android silently blocks Notification.requestPermission() outside a gesture.
 * This primes permission so it shows the dialog immediately on tap.
 */
export async function primeNotificationPermission() {
  if (isNative()) return; // native handled by startWorkoutNotification
  if (typeof Notification === 'undefined') return;
  if (Notification.permission === 'default') {
    await Notification.requestPermission();
  }
}

/**
 * Call when a workout starts.
 * Requests permission, registers background/foreground listeners.
 */
export async function startWorkoutNotification(info) {
  _lastInfo = info;

  if (isNative()) {
    try {
      const perm = await LocalNotifications.requestPermissions();
      if (perm.display === 'denied') { _disabled = true; return; }
    } catch {
      _disabled = true;
      return;
    }
    _disabled = false;
    registerTapListener();
    registerAppStateListener();
    if (_isBackground) await postWorkoutNotification(info);
  } else {
    // PWA / web path
    const granted = await requestWebPermission();
    if (!granted) return;
    registerVisibilityListener();
    if (document.hidden) postWebWorkoutNotification(info);
  }
}

/**
 * Call when exercise name, set number, or rest state changes.
 * Only posts if app is currently in background.
 */
export async function updateWorkoutNotification(info) {
  if (_disabled) return;
  _lastInfo = info;
  if (!_isBackground) return;

  if (isNative()) {
    await postWorkoutNotification(info);
  } else {
    postWebWorkoutNotification(info);
  }
}

/**
 * Call when rest timer reaches 0 and app is in background.
 * Posts a one-shot "Rest Complete" notification.
 */
export async function scheduleRestCompleteNotification(exerciseName) {
  if (_disabled || !_isBackground) return;

  if (isNative()) {
    try {
      await LocalNotifications.schedule({
        notifications: [
          {
            id: REST_COMPLETE_NOTIFICATION_ID,
            title: '✅ Rest Complete',
            body: `Back to ${exerciseName}!`,
            schedule: { at: new Date(Date.now() + 50) }, // +50ms: LocalNotifications requires a future timestamp
            ongoing: false,
            autoCancel: true,
            smallIcon: 'ic_stat_icon',
            iconColor: '#FF5C00',
            extra: { type: 'rest_complete' },
          },
        ],
      });
    } catch (e) {
      console.warn('[workoutNotification] scheduleRestCompleteNotification error', e);
    }
  } else {
    // PWA / web path
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    try {
      const reg = await getSwRegistration();
      if (reg) {
        await reg.showNotification('✅ Rest Complete', {
          body: `Back to ${exerciseName}!`,
          tag: 'rest-complete',
          silent: false,
          icon: '/icon-192.png',
          badge: '/icon-192.png',
          data: { url: '/workout' },
        });
      } else {
        const n = new Notification('✅ Rest Complete', {
          body: `Back to ${exerciseName}!`,
          tag: 'rest-complete',
          icon: '/icon-192.png',
        });
        n.onclick = () => { window.focus(); n.close(); };
      }
      // Update the persistent notification back to active-set state
      if (_lastInfo) postWebWorkoutNotification({ ..._lastInfo, isResting: false });
    } catch (e) {
      console.warn('[workoutNotification] scheduleRestCompleteNotification (web) error', e);
    }
  }
}

/**
 * Call when the workout ends.
 * Cancels all notifications and resets state.
 */
export async function stopWorkoutNotification() {
  _lastInfo = null;
  _disabled = false;

  if (isNative()) {
    try {
      await LocalNotifications.cancel({
        notifications: [
          { id: WORKOUT_NOTIFICATION_ID },
          { id: REST_COMPLETE_NOTIFICATION_ID },
        ],
      });
    } catch (e) {
      console.warn('[workoutNotification] stopWorkoutNotification error', e);
    }
  } else {
    closeWebWorkoutNotification();
  }
}
